// DEPENDECIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
// COMPONENTS
import MUIDataTable from 'mui-datatables';
import { Button, Modal, Form, Nav } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
import Select from 'react-select';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../redux/confirmModalRedux';
import AiChatDiagnostics from '../../components/AI/AiChatDiagnostics';
// SERVICES AND HELPERS
import * as settingService from '../../../services/management/settingService';
import { getAllUsers } from '../../../services/management/userService';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import SettingValidator from '../../../helpers/validators/SettingValidator';
import { getInputClasses } from '../../../helpers/helper';
import {
  filterSettingsForTab,
  isAiAllowedUsersSetting,
  isDoclingApiKeySetting,
} from './settingPresentation';

var initialSetting = {
  id: 0,
  key: '',
  value: '',
};

const parseUserIds = (value) => {
  return (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => /^\d+$/.test(item))
    .map((item) => Number(item))
    .filter((id, index, ids) => ids.indexOf(id) === index);
};

const getSettingsTab = (search) => {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'ai' ? 'ai' : 'general';
};

const SettingsListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal } = props;
  const [data, setData] = useState([]);
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getSettingsTab(location.search));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(initialSetting);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    loadTableData();

    getAllUsers(false, true)
      .then((users) => {
        setUserOptions(
          users.map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} (${user.role}) | ${user.userName} | ${user.email}`,
            selectedLabel: `${user.firstName} ${user.lastName} (${user.role})`,
            name: `${user.firstName} ${user.lastName}`,
          })),
        );
      })
      .catch(() => setUserOptions([]))
      .finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    setActiveTab(getSettingsTab(location.search));
  }, [location.search]);

  const changeTab = (tab) => {
    const nextTab = tab || 'general';
    if (nextTab === activeTab) return;

    const currentLocation = history.location;
    const params = new URLSearchParams(currentLocation.search);
    params.set('tab', nextTab);

    history.push({ ...currentLocation, search: `?${params.toString()}` });
    setActiveTab(nextTab);
  };

  const loadTableData = () => {
    settingService
      .getSettings()
      .then((res) => {
        setData(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const editSetting = (id) => {
    settingService.getSettingById(id).then((res) => {
      setSelectedSetting(res);
      setIsModalOpen(true);
    });
  };

  const deleteSetting = (id) => {
    settingService.deleteSetting(id).then((res) => {
      showAlert({ message: res.message, variant: 'success' });
      loadTableData();
      hideConfirmModal();
    });
  };

  const openNewSettingModal = () => {
    setSelectedSetting({
      ...initialSetting,
      key: activeTab === 'ai' ? 'AI:' : '',
    });

    setIsModalOpen(true);
  };

  const columns = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 15,
        download: false,
        setCellProps: () => ({ style: { width: '20%' }, 'data-aes-field': 'actions' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'actions' }),
        customBodyRender: (id) => {
          return (
            <div className='aes-admin-listing-actions'>
              <button className='btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5' onClick={() => editSetting(id)}>
                Edit
              </button>
              <button
                onClick={() =>
                  showConfirmModal({
                    title: 'Delete Setting',
                    text: 'Are you sure you want to delete this setting?',
                    rightBtnText: 'Confirm',
                    btnAction: () => deleteSetting(id),
                  })
                }
                className='btn btn-sm btn-danger font-weight-bold py-2 px-3 px-xxl-5 my-1'
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
    {
      name: 'key',
      label: 'Key',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'primary', 'data-aes-label': 'Key' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'primary' }),
      },
    },
    {
      name: 'value',
      label: 'Value',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'detail', 'data-aes-label': 'Value' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'detail' }),
        customBodyRender: (value, tableMeta) => {
          const key = tableMeta.rowData[1];

          if (isAiAllowedUsersSetting(key)) {
            const names = parseUserIds(value).map((userId) => {
              const user = userOptions.find((option) => option.value === userId);
              return user ? user.name : `User ID ${userId}`;
            });

            return <div style={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 5 }}>{names.join(', ')}</div>;
          }

          return <div style={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 5 }}>{value}</div>;
        },
      },
    },
  ];

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSetting(initialSetting);
    formik.resetForm();
  };

  const saveSetting = (values, setStatus, setSubmitting) => {
    const settingToSave = isAiAllowedUsersSetting(values.key)
      ? { ...values, value: parseUserIds(values.value).join(',') }
      : values;

    settingService
      .saveSetting(settingToSave)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
        loadTableData();
        handleModalClose();
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: selectedSetting,
    validationSchema: SettingValidator,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      saveSetting(values, setStatus, setSubmitting);
    },
  });

  const selectedUserOptions = parseUserIds(formik.values.value).map((userId) => {
    return userOptions.find((option) => option.value === userId) || {
      value: userId,
      label: `User ID ${userId} (inactive or unavailable)`,
    };
  });

  const filteredData = filterSettingsForTab(data, activeTab);

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--settings'>
      <Modal centered size='lg' show={isModalOpen} onHide={handleModalClose}>
        <form className='card card-custom' onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Setting</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='form'>
              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Key</label>
                <div className='col-lg-9 col-xl-9'>
                  <div className='input-group input-group-lg input-group-solid'>
                    <input
                      placeholder='Start typing...'
                      className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'key')}`}
                      name='key'
                      disabled={selectedSetting.id > 0 && isDoclingApiKeySetting(selectedSetting.key)}
                      {...formik.getFieldProps('key')}
                    />
                  </div>
                  {formik.touched.key && formik.errors.key ? (
                    <div className='invalid-feedback display-block'>{formik.errors.key}</div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Value</label>
                <div className='col-lg-9 col-xl-9'>
                  {isAiAllowedUsersSetting(formik.values.key) ? (
                    <Select
                      value={selectedUserOptions}
                      options={userOptions}
                      isMulti
                      isLoading={usersLoading}
                      closeMenuOnSelect={false}
                      placeholder='Select active users...'
                      name='value'
                      formatOptionLabel={(option, { context }) => context === 'value' ? option.selectedLabel : option.label}
                      onBlur={() => formik.setFieldTouched('value', true)}
                      onChange={(selectedOptions) => {
                        formik.setFieldValue('value', (selectedOptions || []).map((option) => option.value).join(','));
                      }}
                    />
                  ) : isDoclingApiKeySetting(formik.values.key) ? (
                    <div className='input-group input-group-lg input-group-solid'>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        placeholder='Leave blank to keep the configured key'
                        className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'value')}`}
                        name='value'
                        {...formik.getFieldProps('value')}
                      />
                    </div>
                  ) : (
                    <div className='input-group input-group-lg input-group-solid'>
                      <Form.Control
                        as='textarea'
                        rows='10'
                        placeholder='Start typing...'
                        className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'value')}`}
                        name='value'
                        {...formik.getFieldProps('value')}
                      />
                    </div>
                  )}

                  {formik.touched.value && formik.errors.value ? (
                    <div className='invalid-feedback display-block'>{formik.errors.value}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant='secondary' onClick={handleModalClose}>
              Close
            </Button>
            <button type='submit' className='btn btn-success mr-2' disabled={formik.isSubmitting || (formik.touched && !formik.isValid)}>
              Save
              {formik.isSubmitting}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <div className='col-lg-12 col-xxl-12'>
        <div className='card card-custom card-stretch gutter-b'>
          <div className='card-header border-0 py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark'>Settings</span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View and Manage Settings</span>
            </h3>

            <div className='card-toolbar'>
              <button onClick={openNewSettingModal} className='btn btn-success font-weight-bolder font-size-sm mr-3'>
                <span className='svg-icon svg-icon-lg svg-icon-white'>
                  <SVG src={toAbsoluteUrl('/media/svg/icons/Code/Plus.svg')} />
                </span>{' '}
                Add Setting
              </button>
            </div>
          </div>

          <div className='card-header border-0'>
            <Nav variant='pills' activeKey={activeTab} onSelect={changeTab}>
              <Nav.Item>
                <Nav.Link eventKey='general' className="fz-xs">General</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey='ai' className="fz-xs">AI Settings</Nav.Link>
              </Nav.Item>
            </Nav>
            {activeTab === 'ai' && <AiChatDiagnostics showAlert={showAlert} />}
          </div>

          <div className='container-fluid aes-admin-listing p-0'>
            <MUIDataTable
              key={activeTab}
              className='aes-admin-listing-surface'
              data={filteredData}
              columns={columns}
              options={{
                responsive: 'standard',
                rowsPerPage: 25,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table',
                }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { ...alert.actions, ...confirmModal.actions })(SettingsListingPage);
